import { Network } from "configs/web3";
import { ErrorResult } from "services/types";
import { SurveyResponseItem } from "store/business/campaigns/types";
import { SurveyTags } from "../survey-tags/types";
import { WhitelistedEmail } from "../whitelist/types";

export type CreateSurveyState = {
  pending: boolean;
  error: ErrorResult | null;
  surveyDetail?: SurveyDetailResponseItem;
  surveyData?: SurveyData;
  paymentMethod?: PaymentMethod;
  paymentCryptoType: PaymentCryptoType;
  paymentNetwork?: PaymentNetwork;
  surveyGenerateType?: SurveyGenerateTypeEnum;
  answers: SurveyAnswers;
  participants: number;
  reward: number;
  totalRewardAmount: number;
  surveyObjective?: SurveyObjectiveEnum;
  surveyType?: SurveyTypeEnum;
  cryptoPaymentData: SurveyPaymentTrxData | null;
  fiatPaymentData: SurveyPaymentFiatData | null;
  agoricPaymentData: AgoricPaymentTrxData | null;
  agoricSelectedChains: string[];
  agoricSelectedDenom: string | null;
  paymentTransaction?: PaymentTransaction | null;
  paymentConfirmation?: TransactionConfirmationStatus | null;
};

export type PaymentTransaction = SurveyTransactionData[];

export type SurveyAnswers = { [key: string]: string | boolean | string[] };

export enum PaymentMethod {
  CRYPTO = "crypto",
  CARD = "card",
  FIAT = "fiat",
}

export enum PaymentCryptoType {
  native = "native",
  nft = "nft",
}

export enum SurveyGenerateTypeEnum {
  quiz = "quiz",
  campaign = "campaign",
}

export type PaymentNetwork = {
  image: string;
  chainId: number | string;
  network: Network;
  networkName: string;
  token: string;
  minAmount?: number;
  maxAmount?: number;
  isNFT?: boolean;
  isViemChain?: boolean;
};

export enum SurveyObjectiveEnum {
  qualitative = "QUALITATIVE",
  quantitative = "QUANTITATIVE",
}

export enum SurveyTypeEnum {
  private = "private",
  public = "public",
}

export type SurveyData = {
  title?: string;
  description?: string;
  logoPosition?: string;
  logo?: string;
  pages?: SurveyPage[];
};

export type SurveyPage = {
  name: string;
  elements: SurveyPageElement[];
  title: string;
  description: string;
};

export type SurveyChoice = {
  value: string;
  text?: string;
};

export type SurveyPageElement = {
  type: SurveyElement;
  name: string;
  title?: string;
  correctAnswer: string | boolean | string[];
  choices?: Array<SurveyChoice | string>;
  choicesOrder?: "random" | "asc" | "desc";
  isRequired?: boolean;
};

export enum SurveyType {
  quiz = "quiz",
  campaign = "campaign",
}

export enum SurveyElement {
  radiogroup = "radiogroup",
  text = "text",
  boolean = "boolean",
  checkbox = "checkbox",
}

export type CreateSurveyResponse = {
  id: string;
};

export interface CreateSurveyPayload extends SurveyData {
  isFree?: boolean;
  isQuiz?: boolean;
  isPrivate?: boolean;
  isQualitative?: boolean;
  score?: number;
  isMultiAttempt?: boolean;
  useDataProtector?: boolean;
}

export type CreateSurveyWithAIPayload = {
  keyword: string;
  questionCount: number;
  isQuiz?: boolean;
  aiModel: string;
};

export type UpdateSurveyPayload = {
  data: CreateSurveyPayload | FormData;
  id: string;
};

export type SurveyInfoPayload = {
  id: string;
  isAnon?: boolean;
  data?: {
    txHash: string;
  };
};

export type DeleteSurveyPayload = {
  id: string;
};

export type SendInvitesPayload = {
  surveyId: string;
  data: {
    emails: string[];
  };
};

export interface SurveyDetailResponseItem extends SurveyResponseItem {
  pages: SurveyPage[];
  tags: SurveyTags[];
  whitelists: WhitelistedEmail[];
  pendingNftReward: boolean;
  pendingNativeCryptoReward: boolean;
}

export type SurveyPaymentData = {
  data: SurveyPaymentTrxDataPayload;
  isTonProof: boolean;
  isSolanaProof: boolean;
  isRootstockProof: boolean;
  isKakarotProof: boolean;
  isBobaProof: boolean;
  isSkaleProof: boolean;
  isNearProof: boolean;
  isLiskProof: boolean;
  isAgoricProof: boolean;
  isArbitrumProof: boolean;
};

export type AgoricChainData = {
  chain: string;
  denom: string;
  participantsLimit: number;
  rewardAmount: string;
};

export type AgoricPaymentData = {
  data: AgoricPaymentTrxDataPayload;
  isAgoricProof: boolean;
};

export type AgoricPaymentTrxDataPayload = SurveyPaymentTrxDataPayload & {
  chains: AgoricChainData[];
  denom: string;
};

export type SurveyPaymentFiatDataPayload = {
  surveyId: string;
  participantLimit: number;
  currencyType: PaymentMethod;
  currencyCode: string;
  amount: string;
};

export type SurveyPaymentTrxDataPayload = {
  surveyId: string;
  participantLimit: number;
  chainId: string;
  currencyType: PaymentMethod;
  currencyCode: PaymentNetwork["token"];
  amount: string;
  walletAddress?: string;
};

export type SurveyTransactionPayload = {
  surveyId: string;
};

export type GetPaymentTrxData = {
  data: SurveyTransactionPayload;
  isTonProof?: boolean;
  isSolanaProof?: boolean;
  isRootstockProof?: boolean;
  isKakarotProof?: boolean;
  isSkaleProof?: boolean;
  isBobaProof?: boolean;
  isLiskProof?: boolean;
  isAgoricProof?: boolean;
  isArbitrumProof?: boolean;
};

export type ConfirmSurveyPaymentTrxPayload = {
  surveyId: string;
  txHash: string;
};

export type ConfirmPaymentData = {
  data: ConfirmSurveyPaymentTrxPayload;
  isTonProof?: boolean;
  isSolanaProof?: boolean;
  isRootstockProof?: boolean;
  isKakarotProof?: boolean;
  isSkaleProof?: boolean;
  isBobaProof?: boolean;
  isLiskProof?: boolean;
  isArbitrumProof?: boolean;
};

export type SurveyPaymentTrxData = {
  signature: `0x${string}`;
  token: `0x${string}`;
  timeToExpire: number;
  message: `0x${string}`;
  owner: string;
  surveyId: string;
  participantsLimit: number;
  rewardAmount: string;
  surveyHash: `0x${string}`;
  amountFee: string;
};

export type SurveyPaymentFiatData = {
  timeToExpire: number;
  message: string;
  owner: string;
  surveyId: string;
  participantsLimit: number;
  rewardAmount: string;
  paymentId: string;
  amountFee: string;
};

export type AgoricMessageData = {
  type: number;
  chainType: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any;
  destinationChain: string;
  amountFee: string;
  amountForChain: string;
};

export type AgoricPaymentTrxData = {
  contractAddress: string;
  surveyId: string;
  messages: AgoricMessageData[];
  token: string;
  timeToExpire: number;
  owner: string;
  participantsLimit: number;
  amountFee: string;
  rewardAmount: string;
  denom: string;
  sponsorAddress: string;
};

export type PaymentAnsrSurveyPayload = {
  surveyId: string;
  data: PaymentAnsrSurveyData;
};

export type PaymentAnsrSurveyData = {
  amount: string;
  participantLimit: number;
};

export type SetSurveyAnswersPayload = {
  key: string;
  value: string | boolean | string[];
};

export enum TransactionStatusEnum {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

export enum TransactionTypeEnum {
  SURVEY_PAYMENT = "SURVEY_PAYMENT",
  DEPOSIT = "DEPOSIT",
  WITHDRAWAL = "WITHDRAWAL",
  SUBSCRIPTION = "SUBSCRIPTION",
}

export enum TransactionCurrencyTypeEnum {
  CRYPTO = "CRYPTO",
  FIAT = "FIAT",
}

export type SurveyTransactionData = {
  id: string;
  type: TransactionTypeEnum;
  status: TransactionStatusEnum;
  txHash?: string;
  chainId?: string;
  currencyType: TransactionCurrencyTypeEnum;
  currencyCode: string;
  amount: string;
  amountInUsd: string;
  agoricChain?: string;
  destinationChain?: string;
  failureReason?: string;
  failureMessage?: string;
  lastCheckedAt?: string;
  blockHeight?: number;
  createdAt: string;
  updatedAt: string;
};

export type TransactionConfirmationStatus = {
  allConfirmed: boolean;
  confirmedChains: string[];
  pendingChains: string[];
  failedChains: Array<{
    chain: string;
    reason: string;
    message: string;
  }>;
  txHash: string;
};

export type SurveyPaymentConfirmResponse = {
  status: string;
  metadata?: {
    transactionStatus?: TransactionConfirmationStatus;
  };
};
